import React, { createContext, useContext, useMemo } from 'react';
import { transformSuccessQuery } from '../transformSuccessQuery';
import FilteredDataBuilder, {
  SemFilteredDataBuilder,
} from './FilteredDataBuilder';
import { useFilterRequest } from '@components/Filters/Filters/FiltersProviders';
import {
  useSortedItinerariesQuery,
  useSorting,
} from '@components/Sorting/Provider';
import { isItineraryVisiblePrimeDayTab } from 'clientState/utilsHelper';
import { useSemSortedItinerariesQuery } from '../SEM/SemSortingProvider';
import { useSemFilterRequest } from '../SEM/SemFiltersProvider';

const LOADING_STATE = { loading: true, data: null, error: null };

const FilteredItinerariesContext = createContext(LOADING_STATE);

export const useFilteredItinerariesQuery = () =>
  useContext(FilteredItinerariesContext);

const getBestItineraries = ({ data }) => data?.bestItineraries;
const getHighlightedItineraries = ({ data }) => data?.highlightedItineraries;
export const useBestItinerariesQuery = () => {
  const queryState = useFilteredItinerariesQuery();
  return transformSuccessQuery(queryState, getBestItineraries);
};

export const useHighlightedItinerariesQuery = () => {
  const queryState = useFilteredItinerariesQuery();
  return transformSuccessQuery(queryState, getHighlightedItineraries);
};

const NO_SEARCH_ITINERARIES = {
  filteredItinerariesNumber: 0,
  totalItinerariesNumber: 0,
};

const LOADING_ITINERARY_MATCHES = {
  loading: true,
  error: null,
  ...NO_SEARCH_ITINERARIES,
};

const getError = (error) => ({
  loading: false,
  error,
  ...NO_SEARCH_ITINERARIES,
});

export const useItineraryMatchesQuery = () => {
  const { loading, error, data } = useFilteredItinerariesQuery();
  const { totalItinerariesNumber, totalItinerariesByProduct, itineraries } =
    data || {};
  const { sorting: sortBy } = useSorting();
  const renderedItineraries = itineraries?.filter((itinerary) =>
    isItineraryVisiblePrimeDayTab(itinerary, sortBy)
  );
  const renderedItinerariesNumber = renderedItineraries?.length;
  return useMemo(() => {
    if (loading) {
      return LOADING_ITINERARY_MATCHES;
    }
    if (error) {
      return getError(error);
    }
    return {
      filteredItinerariesNumber: renderedItinerariesNumber,
      totalItinerariesNumber,
      totalItinerariesByProduct,
    };
  }, [
    loading,
    error,
    renderedItinerariesNumber,
    totalItinerariesNumber,
    totalItinerariesByProduct,
  ]);
};

export const useEmptyFilteredItineraries = () => {
  const { filteredItinerariesNumber, totalItinerariesNumber } =
    useItineraryMatchesQuery();
  return useMemo(
    () => totalItinerariesNumber > 0 && filteredItinerariesNumber === 0,
    [filteredItinerariesNumber, totalItinerariesNumber]
  );
};

const calculateFilteredData = ({ data }, filterRequest) => {
  const filteredDataBuilder = FilteredDataBuilder(data, filterRequest);

  data.itineraries.forEach((itinerary) => filteredDataBuilder.add(itinerary));

  return filteredDataBuilder.build();
};

const FilteredItinerariesProvider = ({ children }) => {
  const queryState = useSortedItinerariesQuery();
  const filterRequest = useFilterRequest();
  const nextState = transformSuccessQuery(
    queryState,
    calculateFilteredData,
    filterRequest
  );
  return (
    <FilteredItinerariesContext.Provider value={nextState}>
      {children}
    </FilteredItinerariesContext.Provider>
  );
};

const SemFilteredItinerariesContext = createContext(LOADING_STATE);

export const useSemItineraryMatchesQuery = () => {
  const { loading, error, data } = useSemFilteredItinerariesQuery();
  const { totalItinerariesNumber, itineraries } = data || {};
  const { sorting: sortBy } = useSorting();
  const renderedItineraries = itineraries?.filter((itinerary) =>
    isItineraryVisiblePrimeDayTab(itinerary, sortBy)
  );
  const renderedItinerariesNumber = renderedItineraries?.length;
  return useMemo(() => {
    if (loading) {
      return LOADING_ITINERARY_MATCHES;
    }
    if (error) {
      return getError(error);
    }
    return {
      filteredItinerariesNumber: renderedItinerariesNumber,
      totalItinerariesNumber,
    };
  }, [loading, error, renderedItinerariesNumber, totalItinerariesNumber]);
};

export const useSemFilteredItinerariesQuery = () =>
  useContext(SemFilteredItinerariesContext);

const calculateSemFilteredData = ({ data }, filterRequest) => {
  const semFilteredDataBuilder = SemFilteredDataBuilder(data, filterRequest);

  data.itineraries.forEach((itinerary) =>
    semFilteredDataBuilder.add(itinerary)
  );
  return semFilteredDataBuilder.build();
};

export const SemFilteredItinerariesProvider = ({ children }) => {
  const queryState = useSemSortedItinerariesQuery();
  const filterRequest = useSemFilterRequest();
  const nextState = transformSuccessQuery(
    queryState,
    calculateSemFilteredData,
    filterRequest
  );

  return (
    <SemFilteredItinerariesContext.Provider value={nextState}>
      {children}
    </SemFilteredItinerariesContext.Provider>
  );
};

export default FilteredItinerariesProvider;
